import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import cx from 'clsx';
import { Button, useTransitionEnd } from '@kcd/design-system';

import { useSnackbarContext } from './context';
import type { SnackbarProps } from './types';

const defaultButtonProps: SnackbarProps['buttonProps'] = {
  size: 'xs',
  variant: 'primary',
  children: '확인',
};

export function Snackbar({
  id,
  decoration,
  text,
  closeInMilliseconds,
  className,
  buttonProps,
  offset,
  onClick,
}: SnackbarProps) {
  const { onDelete } = useSnackbarContext();
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const btnProps: SnackbarProps['buttonProps'] = {
    ...defaultButtonProps,
    ...buttonProps,
  };

  const handleClose = useCallback(() => {
    const el = ref.current;
    if (el) {
      el.style.opacity = '0';
    }
  }, []);

  const onClickButton = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (onClick) {
        onClick(e);
        handleClose();
        if (timer.current) {
          clearTimeout(timer.current);
        }
      }
    },
    [handleClose, onClick],
  );

  useTransitionEnd({
    ref,
    propertyName: 'opacity',
    onComplete: onDelete,
  });

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      handleClose();
    }, closeInMilliseconds);

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [closeInMilliseconds, handleClose]);

  const bottomPosition = useMemo(() => {
    if (offset && typeof offset === 'number') {
      return `${offset}px`;
    }

    return offset ? '122px' : '16px';
  }, [offset]);

  return (
    <div
      key={id}
      ref={ref}
      className={cx(
        'mx-20 flex w-full max-w-[calc(100%-40px)] animate-fadeIn items-center rounded-16 bg-secondary-heavy p-16 transition duration-300',
        className,
      )}
      style={{ bottom: bottomPosition, position: 'fixed' }}
    >
      <div className="mr-12 flex-1 overflow-hidden">
        <p className="truncate text-gray-000 opacity-70 typo-caption100-regular">
          {decoration}
        </p>
        <p className="truncate text-gray-000 typo-paragraph100-semibold">
          {text}
        </p>
      </div>
      <Button
        {...buttonProps}
        className="min-h-[44px] flex-none basis-[100px] !px-0 !typo-paragraph100-semibold"
        onClick={onClickButton}
      >
        {btnProps.children}
      </Button>
    </div>
  );
}
